// Fonts:
// https://www.figlet.org/examples.html

import React, { useState, useEffect, useRef } from 'react';
import figlet from 'figlet';
import FONT_FILES from '../fonts';

// Fonts fetched from public/figlet/<name>.flf on first use.
const loaded = new Set();
const inflight = new Map();

// Resolve a requested font to the real file's casing. Case-insensitive hosts
// (dev macOS) tolerate a mismatch; case-sensitive ones (GitLab Pages) 404.
const resolveFontFile = (font) => FONT_FILES[font.toLowerCase()] || font;

// Milliseconds per revealed column of the banner (lower = faster draw).
const DRAW_COL_MS = 6;

const loadFont = (font) => {
  if (loaded.has(font)) return Promise.resolve();
  if (inflight.has(font)) return inflight.get(font);
  const p = fetch(`/figlet/${encodeURIComponent(resolveFontFile(font))}.flf`)
    .then((r) => {
      if (!r.ok) throw new Error('font not found');
      return r.text();
    })
    .then((data) => {
      figlet.parseFont(font, data);
      loaded.add(font);
    });
  inflight.set(font, p);
  return p;
};

// Columns of art that fit the container at the element's current font.
const measureCols = (el) => {
  if (!el) return 80;
  const box = el.closest('div') || el.parentElement;
  const cs = window.getComputedStyle(el);
  const ctx =
    measureCols.ctx ||
    (measureCols.ctx = document.createElement('canvas').getContext('2d'));
  ctx.font = `${cs.fontSize} ${cs.fontFamily}`;
  const chW = ctx.measureText('0').width || 10;
  const avail = (box && box.clientWidth) || window.innerWidth;
  return Math.max(8, Math.floor(avail / chW));
};

const AsciiTitle = ({ text = '', font = 'ANSI Regular' }) => {
  const ref = useRef(null);
  const [lines, setLines] = useState([]);
  const [cols, setCols] = useState(0);

  useEffect(() => {
    let alive = true;
    const build = () => {
      if (!alive) return;
      let art;
      try {
        art = figlet.textSync(text, {
          font,
          width: measureCols(ref.current),
          whitespaceBreak: true
        });
      } catch (e) {
        art = text;
      }
      setLines(art.split('\n'));
      setCols(0);
    };
    loadFont(font)
      .then(build)
      .catch(() => {
        if (alive) {
          setLines([text]);
          setCols(text.length);
        }
      });
    const onResize = () => {
      if (loaded.has(font)) build();
    };
    window.addEventListener('resize', onResize);
    return () => {
      alive = false;
      window.removeEventListener('resize', onResize);
    };
  }, [text, font]);

  const width = lines.reduce((m, l) => Math.max(m, l.length), 0);
  useEffect(() => {
    if (cols >= width) return undefined;
    const id = setTimeout(() => setCols((c) => c + 1), DRAW_COL_MS);
    return () => clearTimeout(id);
  }, [cols, width]);

  const shown = lines.map((l) => l.slice(0, cols)).join('\n');

  return (
    <span
      ref={ref}
      className="ascii-title"
      style={{
        display: 'inline-block',
        whiteSpace: 'pre',
        fontFamily: "'Berkeley','Courier New',monospace",
        lineHeight: 1,
        filter: 'brightness(1.6)'
      }}
    >
      {shown}
    </span>
  );
};

export default AsciiTitle;
